import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useAvPreview } from './useAvPreview';
import type {
  AnalysisResultDataRow,
  AnalysisResultSectionHeader,
  AnalysisRow,
  AnalysisSectionHeader,
  TableGridColumn,
  TableGridRow,
} from '../api/types';
import { useVariablePreviewRenderer } from '../context/VariablePreviewContext';
import type {
  TableShellCell,
  TableShellLabel,
  TableShellProps,
  UseTableShellResult,
} from '../types';
import {
  buildRowsById,
  getColumnGroupLabel,
  getColumnLinkAvId,
  getRowCell,
  ownedSourceName,
} from '../utils/analysisSources';
import { getTableLabel } from '../utils/tableLabel';

// Placeholder for a cell with no value yet (definition shell or unpopulated run).
const PENDING_VALUE = '--';

export const useTableShell = ({
  table,
  tableNumber,
  sources,
}: TableShellProps): UseTableShellResult => {
  const { formatMessage } = useIntl();
  const renderVariablePreview = useVariablePreviewRenderer();
  const { previewAvId, onOpenPreview, onClosePreview } = useAvPreview();

  const rowsById = useMemo(() => buildRowsById(table.rows), [table]);

  // Shared by a section header's own label and a data row's own label -- a
  // results row/header links exactly like its definition-tab counterpart,
  // since it carries the same `uses`/`parentRowId` from the definition row it
  // was merged from. A numeric/string section's statistic or level rows (e.g.
  // "...Mean (SD)" under "Age at index date") share their parent header's
  // variable rather than owning one of their own, so `ownedSourceName` returns
  // null for them and they render as plain text -- the header alone is the
  // click-through point, avoiding a redundant link on every sibling row. A
  // row/header that owns a variable source opens that analytical variable's
  // preview (plain text when no preview renderer is injected); one whose
  // owned source isn't resolved to a variable yet stays plain text.
  const getVariableLabel = useCallback(
    (
      row:
        | AnalysisSectionHeader
        | AnalysisRow
        | AnalysisResultSectionHeader
        | AnalysisResultDataRow,
      testId: string
    ): TableShellLabel => {
      const ownedName = 'uses' in row ? ownedSourceName(row, rowsById) : null;
      const source = ownedName ? sources[ownedName] : undefined;
      if (source?.kind !== 'variable' || !renderVariablePreview) {
        return { label: row.label, onClick: null, testId };
      }
      const avId = source.avId;
      return { label: row.label, onClick: () => onOpenPreview(avId), testId };
    },
    [sources, rowsById, renderVariablePreview, onOpenPreview]
  );

  const getSectionHeader = useCallback(
    (row: AnalysisSectionHeader | AnalysisResultSectionHeader): TableShellLabel =>
      getVariableLabel(row, 'analysis-table-grid-section-link'),
    [getVariableLabel]
  );

  // A data row's own label: clickable once it owns a source resolved to a
  // variable, e.g. "Hallucinations" once bound to its analytical variable.
  const getRowLabel = useCallback(
    (row: AnalysisRow | AnalysisResultDataRow): TableShellLabel =>
      getVariableLabel(row, `analysis-table-grid-row-${row.rowId}-link`),
    [getVariableLabel]
  );

  // A subgroup column's header links to the variable that defines it; a
  // full-cohort column has no such variable and renders as plain text.
  const getColumnHeader = useCallback(
    (column: TableGridColumn): TableShellLabel => {
      const label = getColumnGroupLabel(column);
      const testId = `analysis-table-grid-column-link-${column.columnId}`;
      const avId = label ? getColumnLinkAvId(column, sources) : null;
      if (avId && renderVariablePreview) {
        return { label, onClick: () => onOpenPreview(avId), testId };
      }
      return { label, onClick: null, testId };
    },
    [sources, renderVariablePreview, onOpenPreview]
  );

  // A populated cell shows its value; anything else shows the pending
  // placeholder.
  const getCell = useCallback((row: TableGridRow, column: TableGridColumn): TableShellCell => {
    const cell = getRowCell(row, column.columnId);
    return {
      value: cell?.value ?? PENDING_VALUE,
      isPopulated: cell !== null,
    };
  }, []);

  return {
    title: getTableLabel(table.name, tableNumber, formatMessage),
    characteristicLabel: formatMessage({ id: 'analysisDetail.tables.grid.characteristicLabel' }),
    noRowsLabel: formatMessage({ id: 'analysisDetail.tables.preview.noRows' }),
    columnCount: 1 + table.columns.length,
    getSectionHeader,
    getRowLabel,
    getColumnHeader,
    getCell,
    previewAvId,
    onClosePreview,
    renderVariablePreview,
  };
};
